/**
 * Tabella dei candidati all'acquisto, condivisa fra la pagina dedicata
 * (SPEC-012.2 §4.2) e la sezione "In wishlist" della vista portafoglio (§4.3).
 *
 * Un solo componente e non due: le due superfici mostrano la stessa riga con lo
 * stesso significato, e duplicarla porterebbe due formati della scadenza che col
 * tempo divergono. La colonna "Portafoglio" si nasconde dove è ridondante.
 */

import { Link } from 'react-router-dom';
import { CommercialName } from '@/features/instruments/CommercialName';
import { TagList } from '@/features/tags/TagList';
import { formatDate } from '@/lib/format';
import { Badge, Button, TableWrap } from '@/ui';
import { useDeleteWishlistItem, useUpdateWishlistItem, type WishlistItem } from './api';
import './wishlist.css';
import { InstrumentLogo } from '@/features/instruments/InstrumentLogo';

/** Sotto questa soglia la scadenza smette di essere un dettaglio e diventa un avviso. */
const EXPIRING_SOON_DAYS = 2;

function ExpiryBadge({ item }: { item: WishlistItem }) {
  if (item.expired) return <Badge tone="negative">scaduto</Badge>;
  if (item.daysRemaining <= EXPIRING_SOON_DAYS) {
    return <Badge tone="negative">{item.daysRemaining === 1 ? 'scade domani' : `${item.daysRemaining} giorni`}</Badge>;
  }
  return <span className="cell--minor">{item.daysRemaining} giorni</span>;
}

function ItemRow({
  item,
  showPortfolio,
  onEdit,
}: {
  item: WishlistItem;
  showPortfolio: boolean;
  onEdit: (item: WishlistItem) => void;
}) {
  const update = useUpdateWishlistItem();
  const remove = useDeleteWishlistItem();

  return (
    <tr className={item.expired ? 'wishlist__row--expired' : undefined}>
      <td>
        <div className="wishlist__name">
          {/* Scheda del titolo (SPEC-016): il nome è il punto d'ingresso naturale. */}
          <Link to={`/instruments/${item.instrument.id}`}>
            <InstrumentLogo instrumentId={item.instrument.id} name={item.instrument.name} />
            {item.instrument.name}
          </Link>
        </div>
        {/* F-087: in piccolo sotto il nome. Qui serve a non scambiare due
            candidati con simboli simili prima di comprarne uno. */}
        <CommercialName name={item.instrument.name} commercialName={item.instrument.commercialName} size="small" />
        <div className="cell--minor">
          {item.instrument.symbol ?? item.instrument.isin ?? '—'}
          {/* La riga sopravvive alla rimozione dalla watchlist (§2.2): senza
              questo badge sembrerebbe un dato incoerente invece di una scelta. */}
          {!item.stillInWatchlist && <> · <Badge tone="neutral">non più in watchlist</Badge></>}
        </div>
        {/* Tag in piccolo (F-079): qui servono a riconoscere il tema di un
            candidato a colpo d'occhio, non a modificarlo — si aprono, non si
            editano. La modifica sta nel dettaglio del titolo. */}
        <TagList tags={item.tags} size="small" />
      </td>

      {showPortfolio && (
        <td className="col--secondary">
          <div>{item.portfolio.name}</div>
          <div className="cell--minor">{item.portfolio.baseCurrency}</div>
        </td>
      )}

      <td className="col--secondary">
        {item.maxExposureAmount ? (
          <span className="wishlist__amount">
            {item.maxExposureAmount} {item.maxExposureCurrency}
          </span>
        ) : (
          <span className="cell--minor">nessun limite</span>
        )}
      </td>

      <td className="col--tertiary">
        {item.maxPortfolioPercentage ? (
          <span className="wishlist__amount">{item.maxPortfolioPercentage}%</span>
        ) : (
          <span className="cell--minor">nessun limite</span>
        )}
      </td>

      <td>
        <ExpiryBadge item={item} />
        <div className="cell--minor">{formatDate(item.expiresAt)}</div>
      </td>

      <td>
        <div className="wishlist__actions">
          <Button variant="ghost" onClick={() => onEdit(item)}>
            Modifica
          </Button>
          {/* Il rinnovo non riapre il modal: la finestra la calcola il backend,
              così il frontend non duplica una costante che poi diverge. */}
          <Button
            variant="ghost"
            onClick={() => update.mutate({ id: item.id, renew: true })}
            disabled={update.isPending}
          >
            Rinnova
          </Button>
          <Button variant="ghost" onClick={() => remove.mutate(item.id)} disabled={remove.isPending}>
            Elimina
          </Button>
        </div>
      </td>
    </tr>
  );
}

export function WishlistTable({
  items,
  showPortfolio = true,
  onEdit,
}: {
  items: WishlistItem[];
  showPortfolio?: boolean;
  onEdit: (item: WishlistItem) => void;
}) {
  return (
    <TableWrap>
      <table className="table table--compact">
        <thead>
          <tr>
            <th>Titolo</th>
            {showPortfolio && <th className="col--secondary">Portafoglio</th>}
            <th className="col--secondary">Esposizione max</th>
            <th className="col--tertiary">Peso max</th>
            <th>Scadenza</th>
            <th>Azioni</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item) => (
            <ItemRow key={item.id} item={item} showPortfolio={showPortfolio} onEdit={onEdit} />
          ))}
        </tbody>
      </table>
    </TableWrap>
  );
}
